import { useRef } from "react";
import s from "./MarkdownEditor.module.css";

/**
 * Textarea with a small toolbar for Bold / Italic / Bullet list.
 * Works on top of plain text stored values — no external library.
 *
 * Props mirror a textarea's minimal surface:
 *   value, onChange(v), rows, placeholder, className
 */
export default function MarkdownEditor({ value, onChange, rows = 5, placeholder, className }) {
  const ref = useRef(null);

  function wrapSelection(prefix, suffix = prefix, placeholderText = "") {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end   = ta.selectionEnd;
    const before = value.slice(0, start);
    const selected = value.slice(start, end) || placeholderText;
    const after  = value.slice(end);
    const next   = `${before}${prefix}${selected}${suffix}${after}`;
    onChange(next);
    // Restore a sensible selection once React re-renders.
    requestAnimationFrame(() => {
      ta.focus();
      const newStart = start + prefix.length;
      const newEnd   = newStart + selected.length;
      ta.setSelectionRange(newStart, newEnd);
    });
  }

  function prefixLines(lineMark) {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end   = ta.selectionEnd;
    // Expand selection to whole lines
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    const lineEnd   = (() => {
      const i = value.indexOf("\n", end);
      return i === -1 ? value.length : i;
    })();
    const block = value.slice(lineStart, lineEnd);
    const lines = block.length ? block.split("\n") : [""];
    const allAlreadyPrefixed = lines.every(ln => ln.startsWith(lineMark));
    const newBlock = lines
      .map(ln => (allAlreadyPrefixed ? ln.slice(lineMark.length) : `${lineMark}${ln}`))
      .join("\n");
    const next = value.slice(0, lineStart) + newBlock + value.slice(lineEnd);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(lineStart, lineStart + newBlock.length);
    });
  }

  return (
    <div className={`${s.wrap} ${className || ""}`}>
      <div className={s.toolbar} role="toolbar" aria-label="Text formatting">
        <button type="button" className={s.btn} onClick={() => wrapSelection("**", "**", "bold text")} title="Bold (wrap with **)">
          <strong>B</strong>
        </button>
        <button type="button" className={s.btn} onClick={() => wrapSelection("*", "*", "italic text")} title="Italic (wrap with *)">
          <em>I</em>
        </button>
        <span className={s.sep} />
        <button type="button" className={s.btn} onClick={() => prefixLines("- ")} title="Bulleted list">
          <span className={s.bulletIcon}>• List</span>
        </button>
        <span className={s.hint}>
          Use <code>**bold**</code>, <code>*italic*</code>, blank lines for paragraphs.
        </span>
      </div>
      <textarea
        ref={ref}
        className={s.textarea}
        rows={rows}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}
